import { QueryKey, useMutation, useQueryClient } from '@tanstack/react-query';
import { useDrawerContext } from 'context/hooks';
import { displayNotificationError, displayNotificationSuccess } from 'utils/notifications';

export const useCustomMutation = <TArguments, TResult>(options: {
  fn: (args: TArguments) => Promise<TResult>;
  onSuccess?: {
    invalidateQueryKeys?: QueryKey[];
    message?: string;
    callback?: (result: TResult, variables: TArguments) => void;
    closeDrawer?: boolean;
  };
  onSettled?: {
    invalidateQueryKeys?: QueryKey[];
  };
  onError?: {
    reset?: () => void;
  };
}) => {
  const queryClient = useQueryClient();
  const { fn, onSuccess, onSettled, onError } = options;
  const { closeDrawer } = useDrawerContext();
  return useMutation({
    mutationFn: fn,
    onSuccess: async (result: TResult, variables: TArguments) => {
      if (result instanceof Error) {
        displayNotificationError({ error: result });
        return;
      }
      if (onSuccess?.invalidateQueryKeys) {
        onSuccess?.invalidateQueryKeys.forEach(async (queryKey) => {
          await queryClient.invalidateQueries({ queryKey });
        });
      }
      if (onSuccess?.message) {
        displayNotificationSuccess({ message: onSuccess.message });
      }
      if (onSuccess?.callback) {
        onSuccess.callback(result, variables);
      }
      if (onSuccess?.closeDrawer) {
        closeDrawer();
      }
    },
    onSettled: async () => {
      if (onSettled?.invalidateQueryKeys) {
        onSettled?.invalidateQueryKeys.forEach(async (queryKey) => {
          await queryClient.invalidateQueries({ queryKey });
        });
      }
    },
    onError: async () => {
      if (onError?.reset) {
        onError.reset();
      }
    },
  });
};
